// @TODO: refactor/redesign this
/**
 * Ryan O'Dowd
 * 2018-03-25
 * © Copyright 2018 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Linking,
  Platform,
  View,
} from 'react-native';
import {
  NOTIFICATIONS_ROUTE,
  apiDeleteAccount,
  apiUpdateUser,
  clearLocalVersesForTranslationId,
  fetchVerses,
  logout,
  setLightDarkMode,
  setPreferredScale,
  setTheme,
  updateUserSettings,
} from '@utils/store/actions';
import React, {
  useMemo,
  useState,
} from 'react';
import Button from '@components/common/Button';
import ButtonGroup from '@components/common/ButtonGroup';
import Dialog from '@components/common/Dialog';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import IconButton from '@components/common/IconButton';
import LicensingInfo from '@components/LicensingInfo';
import Menu from '@components/common/Menu';
import ScreenWrapper from '@containers/ScreenWrapper';
import SettingCard from './SettingCard';
import Switch from '@components/common/Switch';
import Text from '@components/common/Text';
import getHeaderStyles from '@components/Header/styles';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import {
  themes,
} from '@utils/hooks/useTheme'; // @TODO: shouldn't need to import this
import utilities from '@utils/utilities';

const Settings = () => {
  const dispatch = hooks.useTypedDispatch();
  const headerStyles = hooks.useStyles(getHeaderStyles);
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  hooks.useBackButton(() => {
    navigation.goBack();
    return true;
  });

  const [_isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const [_isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const [_openTranslation, setOpenTranslation] = useState(null);

  const [_fetchActivePassages, setFetchActivePassages] = useState(true);

  const userSettings = hooks.useTypedSelector((state) => state.userSettings);
  const betaOn = hooks.useTypedSelector((state) => state.userSettings.beta_on);
  const verses = hooks.useTypedSelector((state) => state.verses);
  const currentTheme = hooks.useTypedSelector((state) => state.theme);
  const locales = hooks.useTypedSelector((state) => state.locales);

  const languageIsLoading = hooks.useTypedSelector((state: any) => state.loadingStates.fetchI18n);

  const isAdmin = hooks.useIsAdmin();

  // @TODO: const user = hooks.useTypedSelector((state) => state.oakUser);
  const translationsReducer = hooks.useTypedSelector((state) => state.translations);
  const translations = useMemo(() => {
    return translationsReducer.filter((t) => {
      return isAdmin || t.disabled === 0;
    });
  }, [translationsReducer, isAdmin]);
  const preferredTranslation = hooks.usePreferredTranslation();

  const activePassages = hooks.useTypedSelector((state) => state.goals[state.settingsActiveGoalId]?.passages || [], (a, b) => JSON.stringify(a) === JSON.stringify(b)); // @TODO: get rid of stringify
  const __goalPassagesVerses = hooks.useGetVersesForPassages(activePassages, _fetchActivePassages); // @TODO: this is only here so that passages for active goals are refetched after clearing cache // @TODO: am i crazy, or should the second arg be !_fetchActivePassages? (i think the variable is just named poorly)

  const translationSize = useMemo(() => {
    if (_openTranslation) {
      return utilities.humanReadableBytes(utilities.serializedSizeOfObject(verses[`${_openTranslation.id}`]));
    }

    return '-';
  }, [_openTranslation, verses]);

  const menuItem = (
    <Menu
      statusBarHeight={styles.safeAreaInsets.top}// @TODO: abstract menu to oak component and abstract this prop away
      anchor={
        <Header.Icon
          icon='dots-vertical'
          onPress={() => setIsOverflowMenuOpen(true)}
        />
      }
      visible={_isOverflowMenuOpen}
      onDismiss={() => setIsOverflowMenuOpen(false)}
    >
      <Menu.Item
        title={i18n('deleteAccount')}
        titleStyle={styles.deleteAccountText}
        onPress={() => {
          setIsOverflowMenuOpen(false);
          setIsConfirmDialogOpen(true);
        }}
      />
      <Menu.Item
        title={i18n('signOut')}
        onPress={() => {
          dispatch(logout());
          navigation.goBack();
        }}
      />
    </Menu>
  );

  const isAlreadyDownloaded = translationSize.endsWith('MB') && (+translationSize[0]) >= 7;
  const downloadDisabled = !_openTranslation?.downloadable || isAlreadyDownloaded;

  const backIcon = <Header.BackIcon />;

  return (
    <ScreenWrapper>
      <Header
        leftItems={[backIcon]}
        // @TODO: rightItems={!user.auth_provider_info === 'anonymous' ? [menuItem] : [{}]}
        rightItems={[menuItem]}
        centerComponent={betaOn
          ? (
            <View style={styles.centerComponentWrapper}>
              <View style={styles.centerComponentInner}>
                <Text style={[headerStyles.titleText, styles.betaTitleText]}>{i18n('settings')}</Text>
                <Text style={styles.betaSuperscript}> beta</Text>
              </View>
            </View>
          )
          : null
        }
        title={betaOn ? null : i18n('settings')}
        onTitleLongPress={() => {
          if (isAdmin) {
            dispatch(updateUserSettings({beta_on: +(!betaOn)}));
          }
        }}
      />
      <FullScreenScrollView contentContainerStyle={styles.settingsListContainer}>
        <SettingCard
          key='notifications'
          title={i18n('notificationPreferencesTitle')}
          subtitle={i18n('notificationPreferencesSubtitle')}
          onPress={() => navigation.navigate(NOTIFICATIONS_ROUTE)}
        />

        <SettingCard
          key='lightDarkMode'
          title={i18n('darkModeTitle')}
          subtitle={i18n('darkModeSubtitle')}
          actionItem={
            <Switch
              value={styles.isDarkMode}
              onValueChange={() => {
                const newMode = styles.isDarkMode ? 'light' : 'dark';
                dispatch(setLightDarkMode(newMode));
              }}
            />
          }
        />

        {!!betaOn &&
          <SettingCard
            key='themeColor'
            title={i18n('theme')}
            subtitle={i18n('changeColors')}
            bottomComponent={
              <View>
                <View style={styles.buttonGroupWrapper}>
                  <ButtonGroup
                    options={Object.entries(themes).filter(([__themeName, themeAttributes]: [string, any]) => !themeAttributes.isHidden).map(([themeName, __themeAttributes]) => {
                      return {
                        title: utilities.capitalizeFirstLetter(themeName),
                        value: themeName,
                      };
                    })}
                    isVertical={true}
                    selectedValue={currentTheme}
                    onSelect={(newValue) => {
                      dispatch(setTheme(newValue));
                    }}
                  />
                </View>
              </View>
            }
          />
        }

        <SettingCard
          key='rateUs'
          title={i18n('loveWrinklyTitle')}
          subtitle={i18n('loveWrinklySubtitle', Platform.OS === 'ios' ? 'App Store' : 'Play Store')}
          onPress={() => Linking.openURL(Platform.OS === 'ios' ? globals.appStoreUrl : globals.playStoreUrl)}
        />

        <SettingCard
          key='scale'
          title={i18n('preferredTextSize')}
          subtitle={i18n('preferredTextSizeDescription')}
          bottomComponent={
            <View>
              <View style={styles.buttonGroupWrapper}>
                <ButtonGroup
                  options={['small', 'medium', 'large', 'extraLarge'].map((s) => {
                    let scaleFactor = globals.fontScales.medium;
                    if (s === 'small') {
                      scaleFactor = globals.fontScales.small;
                    } else if (s === 'large') {
                      scaleFactor = globals.fontScales.large;
                    } else if (s === 'extraLarge') {
                      scaleFactor = globals.fontScales.extraLarge;
                    }

                    return {
                      title: i18n(s),
                      value: s,
                      textStyle: {
                        fontSize: 16 * scaleFactor,
                      },
                    };
                  })}
                  selectedValue={Object.entries(globals.fontScales).find(([__k, v]) => v === styles.globalScale)?.[0]}
                  onSelect={(newValue) => dispatch(setPreferredScale(newValue))}
                  isVertical={true}
                />
              </View>
            </View>
          }
        />

        <SettingCard
          key='version'
          title={i18n('preferredTranslation')}
          subtitle={i18n('preferredTranslationBody')}
          bottomComponent={
            <View>
              <View style={styles.buttonGroupWrapper}>
                <ButtonGroup
                  options={translations.map((t) => {
                    return {
                      title: `${t.name} (${t.abbreviation})`,
                      value: t.id,
                      rightItem: (
                        <IconButton
                          key={t.id}
                          style={styles.translationSettingsIcon}
                          icon='cog'
                          iconColor={styles.colors.white}
                          size={20 * styles.globalScale}
                          onPress={() => setOpenTranslation(t)}
                        />
                      ),
                    };
                  })}
                  selectedValue={preferredTranslation.id}
                  onSelect={(newValue) => dispatch(updateUserSettings({preferred_translation_id: newValue}))}
                  isVertical={true}
                />
              </View>
            </View>
          }
        />

        {!!(betaOn || userSettings.locale_id === 2) &&
          <SettingCard
            key='language'
            title={i18n('languageTitle')}
            subtitle={i18n('languageSubtitle')}
            bottomComponent={
              <View>
                <View style={styles.buttonGroupWrapper}>
                  <ButtonGroup
                    options={Object.values(locales).map((l: any) => {
                      return {
                        title: l.language_name_local,
                        value: l.id,
                      };
                    })}
                    disabled={languageIsLoading}
                    isVertical={true}
                    selectedValue={userSettings.locale_id}
                    onSelect={(newValue) => dispatch(apiUpdateUser({locale_id: newValue}))}
                  />
                </View>
              </View>
            }
          />
        }
      </FullScreenScrollView>
      {_openTranslation &&
        <Dialog
          visible={_openTranslation}
          onDismiss={() => setOpenTranslation(null)}
        >{/* @TODO: abstract this modal to it's own component and make available in Settings component as well */}
          {/* @TODO: should settings be tab-navigated, grouped by category? */}
          {/* @TODO: dialog too big for some screens when a11y is set to extra large */}
          <Dialog.Title>{_openTranslation.name}</Dialog.Title>
          <Dialog.Content>
            <View style={styles.sectionWrapper}>
              <View style={styles.contentWrapper}>
                {/* @TODO: download status (available, downloaded, partially downloaded, update available) */}
                {/* @TODO: toggle for auto update? actually, that should be default behavior */}
                <View style={styles.section}>
                  <Text style={styles.descriptionText}>{_openTranslation.downloadable ? (isAlreadyDownloaded ? i18n('downloaded', _openTranslation.abbreviation) : i18n('downloadInstructions')) : i18n('notDownloadable')}</Text>
                  <View style={styles.buttonAndSizeWrapper}>
                    <Button
                      mode='contained'
                      icon='download'
                      disabled={downloadDisabled}
                      color={styles.colors.primary}
                      onPress={() => {
                        // @TODO: disable additional click // @TODO: do this before releasing to prod
                        dispatch(fetchVerses(_openTranslation.id));
                      }}
                    >
                      {isAlreadyDownloaded ? i18n('versionDownloaded', _openTranslation.abbreviation) : i18n('downloadVersion', _openTranslation.abbreviation)}
                    </Button>
                    <Text style={styles.sizeText}>{'6-10 MB'}</Text>{/* @TODO: i18n */}{/* @TODO: dynamic...or maybe just like >8 MB */}
                  </View>
                </View>
                <View style={styles.gap} />
                <View style={styles.section}>
                  {translationSize[0] !== '0' && <Text style={styles.descriptionText}>{i18n('deleteInstructions')}</Text>}
                  <View style={styles.buttonAndSizeWrapper}>
                    <Button
                      mode='contained'
                      icon='trash-can'
                      disabled={translationSize === '-' || translationSize[0] === '0'}
                      color={styles.colors.alert}
                      onPress={() => {
                        dispatch(clearLocalVersesForTranslationId(_openTranslation.id)); // @TODO: this will do the passage of the day as well...refetch it?
                        setFetchActivePassages(false);
                        setTimeout(() => setFetchActivePassages(true), 1000); // in case we clear the cache twice in a row @TODO: not ideal, but there's a race condition clearing the redux store
                      }}
                    >
                      {i18n('deleteLocalData')}
                    </Button>
                    <Text style={styles.sizeText}>{translationSize}</Text>{/* @TODO: i18n */}
                  </View>
                </View>
                <View style={styles.gap} />
                <LicensingInfo translationId={_openTranslation.id} />
              </View>
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='contained'
              onPress={() => setOpenTranslation(null)}
            >
              {i18n('done')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isConfirmDialogOpen &&
        <Dialog
          visible={_isConfirmDialogOpen}
          onDismiss={() => setIsConfirmDialogOpen(false)}
        >
          <Dialog.Title>{i18n('deleteAccountTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{i18n('deleteAccountBody')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setIsConfirmDialogOpen(false)}
            >
              {i18n('deleteAccountCancel')}
            </Button>
            <Button
              mode='contained'
              theme={styles.colors.alert}
              onPress={() => {
                dispatch(apiDeleteAccount());
                setIsConfirmDialogOpen(false);
                navigation.goBack();
              }}
            >
              {i18n('deleteAccountConfirm')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
    </ScreenWrapper>
  );
};

export default Settings;
